<template>
<transition name="dialog-fade" @after-enter="showContent = true">
	<div
		v-if="model"
		data-component="BaseDialog"
		class="prompt-dialog | z-2048 fixed inset-0 !max-w-full"
		:class="{
			'': type === 'fullscreen',
			'grid items-end bg-black/50 sm:items-center': type !== 'fullscreen',
		}"
		@click="dismiss('overlay')"
	>
		<transition name="dialog-slide" @after-leave="showContent = false">
			<div
				v-if="showContent"
				class="prompt-dialog-content | relative mx-auto flex max-h-full w-full flex-col overflow-hidden bg-white"
				:class="[contentStyle[type], contentClass]"
				@click.stop
			>
				<div
					v-if="isWindow && !disableTitleBar"
					class="flex shrink-0 items-center gap-2"
					:class="ScreenSize < 1 ? 'py-4 px-5 border-neutral-100 border-b shadow-sm' : 'p-6 justify-between'"
				>
					<template v-if="ScreenSize >= 1">
						<div class="heading-sm" v-html="windowTitle"/>
						<button v-if="displayClose" class="p-[10px] text-neutral-600" :data-test="dataTest && `${dataTest}__close`" @click="dismiss('window')">
							<OIcon name="close" :size="0.75"/>
						</button>
					</template>
					<template v-else>
						<OIcon v-if="displayBack" class="p-1" name="arrow-left" :size="1.5" @click="$emit('go-back')"/>
						<div class="text-base font-bold" v-html="windowTitle"/>
						<div class="grow"/>
						<OIcon v-if="displayClose" :data-test="dataTest && `${dataTest}__close`" class="p-2" name="close" :size="1.5" @click="dismiss('window')"/>
					</template>
				</div>
				<div v-else-if="displayClose" class="z-2 relative flex items-center justify-end" :class="isWindow && disableTitleBar ? 'p-3' : 'mb-5 sm:mb-4 -mx-2'">
					<div v-if="windowTitle" class="text-body-lg md:text-heading-sm grow text-left font-bold" v-html="windowTitle"/>
					<button v-if="displayClose" class="p-[10px] text-neutral-600" :data-test="dataTest && `${dataTest}__close`" @click="dismiss('window')">
						<OIcon name="close" :size="0.75"/>
					</button>
				</div>

				<div class="grow" :class="[isWindow ? 'overflow-y-auto px-4 lg:px-6' : '', contentContainerStyle[type] || '' ]">
					<slot/>
				</div>
				<div v-if="$slots.footer" class="sticky w-full bg-white px-4 py-3 shadow-md lg:px-6">
					<slot name="footer"/>
				</div>
			</div>
		</transition>
	</div>
</transition>
</template>

<script lang="ts">
import { PropType } from 'vue';

export type BaseDialogType = 'default' | 'window' | 'large' | 'fullscreen' | 'overflowable' | 'windowNew';

/*
	Okno dialogowe.
*/
export default {
	name: 'BaseDialog',
	props: {
		/**
		 * Czy modal ma być wyświetlony?
		 */
		modelValue: {
			type: Boolean,
			default: false,
		},

		/**
		 * Zamiast aktualizować model przy wywołaniu akcji, wywołaj `window.history.back()`.
		 */
		historyMode: {
			type: Boolean,
			default: true,
		},

		/**
		 * Czy zamknąć okno po kliknięciu na nakładkę?
		 */
		closeOnOverlay: {
			type: Boolean,
			default: true,
		},

		/**
		 * Czy wyświetlić przycisk do zamknięcia?
		 */
		displayClose: {
			type: Boolean,
			default: true,
		},

		/**
		 * Czy wyświetlić przycisk wywołujący akcję cofnięcia (jeżeli okno posiada tytuł i jesteśmy na mobilce)?
		 */
		displayBack: {
			type: Boolean,
			default: true,
		},

		/**
		 * Czy wyłączyć nagłówek dla typu `window``
		 */
		disableTitleBar: {
			type: Boolean,
			default: false,
		},

		/**
		 * Tytuł okna dialogowego
		 */
		windowTitle: {
			type: String,
			default: '',
		},

		/**
		 * Jakiego stylu użyć dla okna dialogowego?
		 */
		type: {
			type: String as PropType<BaseDialogType>,
			default: 'default',
		},

		/*
 		* Klasy, które powinny być zastosowane do wewnętrznego kontenera
 		*/
		contentClass: {
			type: String,
			default: '',
		},
		/**
		 * Atrybut data test potrzebny testerom
		 */
		dataTest: {
			type: String,
			default: undefined,
		},

		/**
		 * Klucz, który znajdzie się w urlu, jeśli dialog jest otwarty
		 */
		routerKey: {
			type: String,
			default: '',
		},

		/**
		 * Czy wyłączyć pasek przewijania dokumentu?
		 */
		preventDocumentScrolling: {
			type: Boolean,
			default: true,
		},

	},
	emits: ['update:modelValue', 'dismissed', 'opened', 'closed', 'go-back'],
	data() {
		return {
			/**
			 * Pokaż zawartość okna (po zakończeniu animacji nakładki)
			 */
			showContent: this.modelValue,

			/** Styl okna dialogowego */
			contentStyle: {
				default: 'max-w-[564px] rounded-t-lg sm:rounded-b-lg text-center sm:text-left py-6 sm:px-8 px-6',
				window: 'max-w-[600px] sm:max-h-[600px] sm:rounded-lg h-full',
				large: 'max-w-[760px] rounded-t-lg sm:rounded-b-lg text-center sm:text-left py-6 sm:px-8 px-6',
				fullscreen: '!overflow-auto min-h-full',
				overflowable: 'max-w-[564px] rounded-t-lg sm:rounded-b-lg pt-6 sm:px-8 px-6',
				windowNew: 'max-w-[600px] sm:max-h-[600px] sm:rounded-lg h-full p-1 pb-6',
			},

			/** Styl głównego kontenera okna dialogowego */
			contentContainerStyle: {
				default: '',
				window: '',
				large: '',
				fullscreen: '',
				overflowable: 'overflow-y-auto sm:-mt-4 sm:px-8 px-6 sm:-mx-8 -mx-6',
			},

			/** Czy okno zostało otwarte przez query bezpośrednio po otwarciu apki */
			isInitialOpenByQuery: false,
		};
	},
	computed: {
		isWindow() {
			return this.type === 'window' || this.type === 'windowNew';
		},
		model() {
			if (this.routerKey) {
				return this.$route.query[this.routerKey] === 'true';
			}
			return this.modelValue;
		},
	},

	watch: {
		/**
		 * Podczas zmiany stanu wyświetlenia okna, wyślij odpowiedni event o stanie otwarcia dialogu
		 * i zaktualizuj query, jeśli podano routerKey
		 */
		modelValue: {
			handler(value: boolean) {
				if (value) {
					this.disableWindowScroll(true);
					this.$emit('opened');
					if (this.routerKey && !this.isInitialOpenByQuery) {
						this.$router.push({ query: { ...this.$route.query, [this.routerKey]: 'true' } });
					}
				} else {
					this.disableWindowScroll(false);
					this.$emit('closed');
					if (this.routerKey) {
						this.routerClose();
					}
				}
			},
		},
	},

	/**
	 * Po stworzeniu komponentu, zsynchronizuj stan modelu z query i
	 * zainicjuj watcha na zmiany query, jeśli podano routerKey
	 */
	created() {
		if (!this.routerKey) {
			return;
		}

		const initialValue = this.$route.query[this.routerKey] === 'true';
		this.showContent = initialValue;
		this.isInitialOpenByQuery = initialValue;
		this.$emit('update:modelValue', initialValue);
		this.$watch(() => this.$route.query[this.routerKey], (value) => {
			this.$emit('update:modelValue', value === 'true');
		});
	},

	methods: {
		disableWindowScroll(value: boolean) {
			if (!this.preventDocumentScrolling) { return; }
			document.body.style.overflow = value ? 'hidden' : 'auto';
		},

		/**
		 * Po kliknięciu przycisku wysyła odpowiednie zdarzenie (`dismissed`)
		 * @param source Źródło kliknięcia (przycisk, nakładka)
		 */
		dismiss(source: 'window' | 'overlay') {
			if (source === 'overlay' && !this.closeOnOverlay) { return; }
			this.$emit('dismissed', source);
			this.close();
		},

		/**
		 * Zamyka okno dialogowe: w zależności od właściwości `historyMode`
		 * i `routerKey`, ustawia wartość modelu i/lub cofa się w historii
		 */
		close() {
			this.showContent = false;
			if (this.routerKey) {
				this.routerClose();
			} else if (this.historyMode) {
				window.history.back();
			} else {
				this.$emit('update:modelValue', false);
			}
		},

		/**
		 * Zamyka okno dialogowe, kasując klucz z query i cofając się w historii
		 */
		routerClose() {
			if (!this.$route.query[this.routerKey]) {
				return;
			}

			if (this.isInitialOpenByQuery) {
				this.isInitialOpenByQuery = false;
				this.$router.replace({ query: { ...this.$route.query, [this.routerKey]: undefined } });
				return;
			}

			this.$router.back();
		},
	},
};
</script>

<style>
.dialog-fade-enter-active,
.dialog-fade-leave-active {
	transition: opacity 0.15s ease-in-out 0.15s;
}

.dialog-fade-enter-from,
.dialog-fade-leave-to {
	opacity: 0;
}

.dialog-slide-enter-active,
.dialog-slide-leave-active {
	transition: all 0.3s ease;
}

.dialog-slide-enter-from,
.dialog-slide-leave-to {
	opacity: 0;
	transform: translateY(4rem);
}
</style>
